import { useMemo } from 'react';
import { GenericAbortSignal } from 'axios';

import { InfiniteData } from '@tanstack/query-core';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import Notification, { NotificationOptions } from 'models/Notification';
import { BaseQueryParams } from 'services/api/types';
import http from 'services/http';
import { useToastActions } from 'stores/toastStore';

// Kaminari's default page size on the feed (In-App Notifications tech design §4.7): a page with
// fewer than this many rows is the end of history, so the keyset pager stops requesting.
export const NOTIFICATIONS_PAGE_SIZE = 25;

// One shape for both the bell badge and the panel list: the feed carries the GLOBAL unread total
// in meta (filter-independent — the badge number), with no totalRecords/links under keyset (§7 P6).
export type NotificationsFeed = {
  meta: { unreadCount: number };
  data: NotificationOptions[];
};

export type NotificationsPage = {
  meta: { unreadCount: number };
  data: Notification[];
};

export const notificationQueryKeys = {
  // The unread count is derived via `select` off the index query (the API carries it in the index
  // meta — there is no separate count endpoint), so a `count` key would be dead. One key here.
  index: (...rest: any[]) => ['notifications', 'index', ...rest] as const,
};

type IndexParams = {
  unread?: boolean;
  search?: string;
  'cursor.lt'?: string;
} & BaseQueryParams;

export async function indexNotifications(params: IndexParams, signal: GenericAbortSignal) {
  return http.get<NotificationsFeed>('notifications', { params, signal }).then(
    (res): NotificationsPage => ({
      meta: res.data.meta,
      data: res.data.data.map((x) => new Notification(x)),
    })
  );
}

type UpdateParams = { id: string; read: boolean };
export async function updateNotification({ id, read }: UpdateParams) {
  return http.patch<NotificationOptions>(`notifications/${id}`, { read }).then((res) => new Notification(res.data));
}

// Collection PATCH = "mark all read" (batch form of update); the API returns 204 No Content.
export async function markAllNotificationsRead() {
  return http.patch('notifications', { read: true });
}

// The bell badge and the panel's All tab share this base (unfiltered) query so they resolve to a
// single cache entry — same params = same key. Mirrors escalation.ts's useIndexEscalationParams.
function useIndexNotificationParams(overrides: Partial<IndexParams> = {}) {
  return useMemo(
    () => ({
      ...overrides,
    }),
    [overrides]
  );
}

export function useInfiniteNotifications<TSelectResult = InfiniteData<NotificationsPage, string | undefined>>(
  options: {
    select?: (data: InfiniteData<NotificationsPage, string | undefined>) => TSelectResult;
    enabled?: boolean;
  } = {},
  queryParams: Partial<IndexParams> = {}
) {
  const params = useIndexNotificationParams(queryParams);

  return useInfiniteQuery({
    queryKey: notificationQueryKeys.index(params),
    queryFn: ({ pageParam, signal }) =>
      indexNotifications({ ...params, ...(pageParam ? { 'cursor.lt': pageParam } : {}) }, signal),
    initialPageParam: undefined as string | undefined,
    // Keyset "load older": echo the last row's `<createdAt>|<id>` as the next cursor.lt. A short
    // page (fewer than a full page of rows) means there is nothing older to fetch.
    getNextPageParam: (lastPage) => {
      if (lastPage.data.length < NOTIFICATIONS_PAGE_SIZE) return undefined;
      return lastPage.data[lastPage.data.length - 1]?.cursor;
    },
    ...options,
  });
}

// The badge/header number: the global unread count from the base feed's meta. Selecting off the
// shared base query means the bell reads it without an extra request.
export function useNotificationUnreadCount(options: { enabled?: boolean } = {}) {
  return useInfiniteNotifications({
    ...options,
    select: (data) => data.pages?.[0]?.meta.unreadCount ?? 0,
  });
}

// Per-row mark read / mark unread. Per the story-5 contract the API sends NO self-nudge on
// writes, so the client invalidates every notification query on the mutation response to pull the
// fresh feed + unread count (the badge, the header, and both tab lists).
export function useUpdateNotification() {
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.index() });
    },
    onError: () => {
      addToast({ text: 'Something went wrong. Please try again.' });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.index() });
    },
    onError: () => {
      addToast({ text: 'Something went wrong. Please try again.' });
    },
  });
}
